import Vapi from '@vapi-ai/web';
import { useCallback, useEffect, useRef, useState } from 'react';

interface DeviceError {
  type: string;
  action?: string;
  name?: string;
  message?: string;
}

export interface VapiCallState {
  isCallActive: boolean;
  isConnecting: boolean;
  error: string | null;
  callDuration: number;
  messages: Array<{
    id: string;
    role: 'assistant' | 'user';
    text: string;
    timestamp: number;
  }>;
  currentMessage: {
    role: 'assistant' | 'user';
    text: string;
  } | null;
  isSpeaking: boolean;
  lastActivity: string;
}

export interface VapiCallHook {
  callState: VapiCallState;
  startCall: (vapiAgentId: string) => Promise<void>;
  endCall: () => void;
}

export function useVapiCall(): VapiCallHook {
  const [callState, setCallState] = useState<VapiCallState>({
    isCallActive: false,
    isConnecting: false,
    error: null,
    callDuration: 0,
    messages: [],
    currentMessage: null,
    isSpeaking: false,
    lastActivity: ''
  });
  
  const vapiRef = useRef<Vapi | null>(null);
  const callStartTimeRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const startCall = useCallback(async (vapiAgentId: string) => {
    try {
      setCallState(prev => ({ ...prev, isConnecting: true, error: null }));
      
      const publicApiKey = process.env.NEXT_PUBLIC_VAPI_PUBLIC_KEY;
      
      if (!publicApiKey) {
        throw new Error('Vapi public API key not configured. Please set NEXT_PUBLIC_VAPI_PUBLIC_KEY in your environment.');
      }

      if (!vapiAgentId) {
        throw new Error('AI agent does not have a Vapi agent configured');
      }

      // Initialize Vapi instance
      const vapi = new Vapi(publicApiKey);
      vapiRef.current = vapi;

      // Set up event listeners
      vapi.on('call-start', () => {
        console.log('Call started');
        callStartTimeRef.current = Date.now();
        setCallState(prev => ({ 
          ...prev, 
          isCallActive: true, 
          isConnecting: false,
          error: null,
          lastActivity: 'Call connected'
        }));
        
        // Start duration timer
        durationIntervalRef.current = setInterval(() => {
          if (callStartTimeRef.current) {
            const duration = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
            setCallState(prev => ({ ...prev, callDuration: duration }));
          }
        }, 1000);
      });

      vapi.on('call-end', () => {
        console.log('Call ended');
        setCallState(prev => ({ 
          ...prev, 
          isCallActive: false, 
          isConnecting: false,
          error: null,
          lastActivity: 'Call ended'
        }));
        cleanup();
      });

      vapi.on('error', (error: DeviceError) => {
        console.error('Vapi error:', error);
        let errorMessage = error.message || 'An error occurred during the call';
        
        // Handle device-related errors
        if (error.type === 'not-found' || error.action === 'camera-error') {
          errorMessage = 'Could not access microphone. Please ensure your microphone is connected and you have granted browser permissions.';
        }
        
        setCallState(prev => ({ 
          ...prev, 
          error: errorMessage,
          isConnecting: false,
          isCallActive: false
        }));
        cleanup();
      });

      vapi.on('speech-start', () => {
        console.log('Speech started');
        setCallState(prev => ({
          ...prev,
          isSpeaking: true,
                lastActivity: ""
        }));
      });

      vapi.on('speech-end', () => {
        console.log('Speech ended');
        setCallState(prev => ({
          ...prev,
          isSpeaking: false,
                lastActivity: ""
        }));
      });

      vapi.on('message', (message: { type: "transcript", role: "assistant" | "user", transcript: string, transcriptType: "final" | "partial" }) => {
        console.log("message", message);
        
        if (message.type === "transcript") {
            // final messages can be appended to messages array, and current message can be cleared
            if (message.transcriptType === "final") {
              setCallState(prev => ({
                ...prev,
                messages: [...prev.messages, {
                  id: Math.random().toString(36).substring(7),
                  role: message.role,
                  text: message.transcript,
                  timestamp: Date.now()
                }],
                currentMessage: null,
                lastActivity: ""
              }));
            }

            // partial messages can be used to update current message
            if (message.transcriptType === "partial") {
              setCallState(prev => ({
                ...prev,
                currentMessage: {
                  role: message.role,
                  text: message.transcript,
                },
                lastActivity: ""
              }));
            }
          }
        });

      // Add device check before starting call
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasAudioInput = devices.some(device => device.kind === 'audioinput');
        
        if (!hasAudioInput) {
          throw new Error('No microphone found. Please connect a microphone and try again.');
        }

        // Start the call with the assistant ID
        await vapi.start(vapiAgentId);
        
      } catch (error: unknown) {
        console.error('Error starting call:', error);
        const errorMessage = error instanceof Error && error.name === 'NotAllowedError'
          ? 'Microphone access denied. Please allow microphone access in your browser settings.'
          : error instanceof Error ? error.message : 'Failed to start call';
          
        setCallState(prev => ({ 
          ...prev, 
          error: errorMessage,
          isConnecting: false,
          isCallActive: false
        }));
        cleanup();
      }
    } catch (error) {
      console.error('Error starting call:', error);
      setCallState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to start call',
        isConnecting: false,
        isCallActive: false
      }));
      cleanup();
    }
  }, []);

  const endCall = useCallback(() => {
    if (vapiRef.current) {
      vapiRef.current.stop();
    }
    cleanup();
  }, []);

  const cleanup = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    callStartTimeRef.current = null;
    vapiRef.current = null;
  }, []);

  return {
    callState,
    startCall,
    endCall,
  };
} 